"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  isMobile = false,
  onItemClick
}: {
  isMobile?: boolean;
  onItemClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const filteredItems = menuItems.filter(item => item.label !== "Our Model");
  if (loading) {
    return <div className={cn("flex gap-6", isMobile ? "flex-col items-center w-full" : "flex-row")}>
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i === 1 ? 0 : undefined} className={cn("bg-muted animate-pulse rounded-md", isMobile ? "h-8 w-32" : "h-5 w-24")} />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col items-center gap-4 w-full">
        <Button asChild variant="ghost" className="text-lg font-serif font-medium text-white hover:text-white/80 transition-colors w-full text-center py-6 border-b border-white/10 rounded-none" onClick={onItemClick}>
          <Link to="/">Home</Link>
        </Button>
        {filteredItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-serif font-medium text-white hover:text-white/80 transition-colors w-full text-center py-3 border-b border-white/10 last:border-0" onClick={onItemClick} data-index={i === 0 ? 0 : undefined}>
            {item.label}
          </Link>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <Link to="/" className="text-sm font-serif font-medium transition-colors">
              Home
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
        {filteredItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i === 0 ? 0 : undefined}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-serif font-medium transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full border-b transition-all duration-300", isScrolled ? "bg-primary md:bg-background/95 md:backdrop-blur-md border-border/50 shadow-sm" : "bg-primary md:bg-background border-transparent")}>
      
      <div className="container mx-auto px-4 h-20 flex items-center justify-between relative">
        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 text-white font-serif !text-center !italic !text-2xl md:hidden whitespace-nowrap">Resolve Clinical Services</div>
        <Link to="/" className="flex-shrink-0">
          
        </Link>

        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="17">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex-shrink-0 w-48 flex justify-end items-center gap-4">
          <Button asChild className="hidden md:inline-flex bg-white text-primary hover:bg-white/90">
            <Link to="/ReferralsPage">Refer Now</Link>
          </Button>

          <Button variant="ghost" size="icon" className="md:hidden flex relative z-50 text-white hover:text-white/80" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}>
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      <div className={cn("fixed inset-0 bg-background/80 backdrop-blur-sm transition-opacity duration-300 md:hidden", isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")} onClick={() => setIsMobileMenuOpen(false)} />

      {}
      <div className={cn("fixed top-0 right-0 bottom-0 z-50 w-[80%] max-w-sm bg-primary border-l border-border shadow-2xl transition-transform duration-300 ease-in-out transform md:hidden flex flex-col", isMobileMenuOpen ? "translate-x-0" : "translate-x-full")}>
        <div className="flex flex-col items-center gap-8 p-8 mt-20">
          <MenuProvider menu_id="17" className="w-full">
            <HeaderMenuConsumer isMobile onItemClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>

          <div className="w-full max-w-xs h-px bg-border" />

          <Button asChild className="w-full max-w-xs" size="lg">
            <Link to="/ReferralsPage" onClick={() => setIsMobileMenuOpen(false)}>
              Refer Now
            </Link>
          </Button>
        </div>
      </div>
    </header>;
}